"use client"

import type React from "react"

import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { AuthLink } from "@/components/auth/auth-link"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Eye, EyeOff, AlertCircle } from "lucide-react"
import { EnvironmentBadge } from "@/components/environment-badge"
import { usePlatformLogo } from "@/hooks/use-platform-logo"
import { SocialSignInButtons } from "@/components/auth/social-sign-in-buttons"
import { ResendVerification } from "@/components/auth/resend-verification"
import { useTranslations } from "next-intl"


interface LoginFormProps {
  className?: string
  onLogin: (email: string, password: string, rememberMe: boolean) => Promise<{ ok: boolean; code?: string }>
  /** Off inside the Shopify flows, where opening the app from Shopify leads back to the same page. */
  shopifySignIn?: boolean
}

export function LoginForm({ onLogin, className, shopifySignIn = false }: LoginFormProps) {
  const tUi = useTranslations("Auth.LoginForm")

  const { logoUrl: platformLogoUrl, platformName } = usePlatformLogo()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [rememberMe, setRememberMe] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  // Unverified email is a distinct, recoverable state — surface a resend
  // control rather than a dead-end error.
  const [unverified, setUnverified] = useState(false)

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("error")
    if (code) setError(tUi("weCouldnTCompleteSignInPleaseTry"))
  }, [tUi])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setUnverified(false)
    setIsLoading(true)

    try {
      const result = await onLogin(email, password, rememberMe)
      if (!result.ok) {
        if (result.code === "email_not_verified") {
          setUnverified(true)
        } else {
          setError(tUi(result.code === "tenant_archived" ? "organizationDeleted" : "invalidCredentials"))
        }
      }
    } catch {
      setError(tUi("anErrorOccurredDuringLoginPleaseTryAgain"))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn("min-h-screen flex flex-col items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8", className)}>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="mb-4 flex items-center justify-center gap-2">
            <Image
              src={platformLogoUrl}
              alt={tUi("logo")}
              width={120}
              height={40}
              className="h-10 w-auto"
            />
            <EnvironmentBadge />
          </div>
          <CardTitle className="text-2xl font-bold text-center"><span><span key={tUi("signInTo")}>{tUi("signInTo")}</span></span> {platformName}</CardTitle>
          <CardDescription className="text-center">
            <span key={tUi("enterYourEmailAndPasswordToAccessYour")}>{tUi("enterYourEmailAndPasswordToAccessYour")}</span></CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {unverified && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="space-y-3">
                  <p><span key={tUi("pleaseVerifyYourEmailBeforeSigningIn")}>{tUi("pleaseVerifyYourEmailBeforeSigningIn")}</span></p>
                  <ResendVerification email={email} showInput={false} size="sm" />
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email"><span key={tUi("emailAddress")}>{tUi("emailAddress")}</span></Label>
              <Input
                id="email"
                type="email"
                placeholder={tUi("enterYourEmail")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password"><span key={tUi("password")}>{tUi("password")}</span></Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={tUi("enterYourPassword")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  checked={rememberMe}
                  onChange={event => setRememberMe(event.target.checked)}
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <Label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  <span key={tUi("rememberMe")}>{tUi("rememberMe")}</span></Label>
              </div>
              {/* `-my-2` keeps the row height while the padding restores a thumb-sized target. */}
              <AuthLink href="/auth/forgot-password" className="-my-2 py-2 text-sm">
                <span key={tUi("forgotPassword")}>{tUi("forgotPassword")}</span></AuthLink>
            </div>

            <Button type="submit" variant="brand" className="w-full" disabled={isLoading}>
              <span key={isLoading ? tUi("signingIn") : tUi("signIn")}>{isLoading ? tUi("signingIn") : tUi("signIn")}</span>
            </Button>

            <SocialSignInButtons rememberMe={rememberMe} dividerPlacement="before" shopify={shopifySignIn ? "sign-in" : undefined} />
          </form>
        </CardContent>
      </Card>
      <p className="mt-4 text-center text-sm text-muted-foreground">
        <span><span key={tUi("donTHaveAnAccount")}>{tUi("donTHaveAnAccount")}</span></span> <AuthLink href="/auth/register"><span key={tUi("signUp")}>{tUi("signUp")}</span></AuthLink>
      </p>
    </div>
  )
}
